// client/src/context/SettingsContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/axios'; // Use centralized api instance
import AuthContext from './AuthContext';

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
    const { token } = useContext(AuthContext);
    const [settings, setSettings] = useState({
        is_car_model_required: true, // Default value
        default_vehicle_size_id: null,
    });
    const [loading, setLoading] = useState(true);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/shops/settings');
            setSettings(response.data.data);
        } catch (error) {
            console.error("Failed to fetch shop settings:", error);
            // Keep default settings on error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchSettings();
        } else {
            setLoading(false);
        }
    }, [token]);

    const updateSettings = (newSettings) => {
        setSettings(prev => ({ ...prev, ...newSettings }));
    };

    // 구독 상태 기반 파생값
    const subscriptionStatus = settings.subscription_status || 'active';
    const trialEndsAt = settings.trial_ends_at ? new Date(settings.trial_ends_at) : null;
    const trialExpired = trialEndsAt ? trialEndsAt.getTime() < Date.now() : false;
    const trialDaysLeft = trialEndsAt
        ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : null;

    // 미결제 자동 휴면 — 파기 예정일(휴면 전환일 + 30일)을 배너에 보여준다.
    // 재결제하면 해제되므로 '아직 되돌릴 수 있다'는 걸 명확히 알린다.
    const isNonpaymentDormant = settings.dormant_reason === 'nonpayment' && !!settings.dormant_at;
    const purgeDate = isNonpaymentDormant
        ? new Date(new Date(settings.dormant_at).getTime() + 30 * 24 * 60 * 60 * 1000)
        : null;

    // 결제수단(빌링키) 등록 여부 — /shops/settings 의 EXISTS 서브쿼리(1/0)
    const hasPaymentMethod = !!settings.has_payment_method;

    // isReadOnly: trialing이면서 만료된 경우 OR canceled OR none
    // active / trialing(미만료) / past_due 는 일단 쓰기 가능하게 두되, 백엔드 미들웨어가 차단
    const isReadOnly =
        subscriptionStatus === 'canceled' ||
        subscriptionStatus === 'none' ||
        (subscriptionStatus === 'trialing' && trialExpired);

    return (
        <SettingsContext.Provider
            value={{
                settings,
                loading,
                updateSettings,
                fetchSettings,
                subscriptionStatus,
                trialEndsAt,
                trialExpired,
                trialDaysLeft,
                hasPaymentMethod,
                isNonpaymentDormant,
                purgeDate,
                isReadOnly,
                isEarlyBird: !!settings.is_early_bird,
            }}
        >
            {children}
        </SettingsContext.Provider>
    );
};

export default SettingsContext;
